import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Clock, DollarSign, Shield, Zap } from 'lucide-react';
import CommonContactUs from './ContactPage';

const ContractStaffingPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Contract Staffing
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-6">
              Flexible workforce solutions for your business needs
            </p>
            <p className="text-lg text-purple-200">
              Our Services
            </p>
          </motion.div>
        </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-4 mb-6"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500 flex-shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800">
                Contract Staffing Services
              </h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 mb-8"
            >
              Our contract staffing solutions provide businesses with flexible, skilled professionals for short-term and long-term projects. We handle all the administrative burdens while you focus on your core business objectives.
            </motion.p>

            {/* Services Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gray-50 p-6 rounded-xl mb-10"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Our Contract Staffing Solutions
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Project-Based Staffing</h4>
                    <p className="text-gray-600">
                      Hire skilled professionals for specific projects with defined timelines and deliverables. Perfect for seasonal workloads, special projects, or temporary skill gaps.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Long-Term Contracts</h4>
                    <p className="text-gray-600">
                      Extended contract arrangements for ongoing business needs. Our professionals become integrated team members while maintaining the flexibility of contract relationships.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Specialized Skills</h4>
                    <p className="text-gray-600">
                      Access to niche expertise and specialized skills that may not be available in your local market or required for full-time positions.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Scalable Workforce</h4>
                    <p className="text-gray-600">
                      Scale your workforce up or down based on business demands without the commitment and costs associated with permanent hiring.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Benefits Section */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-purple-50 p-6 rounded-xl mb-10"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Benefits of Contract Staffing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Quick Deployment</h4>
                    <p className="text-gray-600">Get skilled professionals on your projects within days, not months.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Cost Control</h4>
                    <p className="text-gray-600">Pay only for the work you need without overhead costs.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Risk Mitigation</h4>
                    <p className="text-gray-600">We handle payroll, benefits, and compliance for contract workers.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Flexibility</h4>
                    <p className="text-gray-600">Scale your workforce based on project demands and business cycles.</p>
                  </div>
                </div>
              </div>
            </motion.div> */}

            {/* Process Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Benefits of Contract Staffing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold mb-3">
                    1
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cost Efficiency</h4>
                  <p className="text-gray-600">
                    Reduce overhead costs associated with full-time employees while still accessing top talent for your projects.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold mb-3">
                    2
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Flexibility</h4>
                  <p className="text-gray-600">
                    {/* We identify qualified candidates from our network and present you with the best matches for your needs. */}
                    Quickly adapt to changing business needs by scaling your workforce up or down as required.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold mb-3">
                    3
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Specialized Expertise</h4>
                  <p className="text-gray-600">
                    Access specialized skills that may not be available within your permanent workforce.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Industries Section */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gray-50 p-6 rounded-xl mb-10"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Industries We Serve
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Technology & IT</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Healthcare</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Finance & Banking</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Manufacturing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Retail & E-commerce</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Consulting</span>
                </div>
              </div>
            </motion.div> */}

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex justify-center gap-4 flex-wrap"
            >
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gray-100 text-gray-800 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-flex items-center"
                >
                  <span>Back to Services</span>
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-300"
                >
                  <Users size={18} />
                  <span>Contact Us</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
      {/* Contact Form Section */}
      {/* <section id="contract-staffing-contact" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Get Started with Contract Staffing
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Ready to find flexible workforce solutions? Contact us today to discuss your contract staffing needs.
              </p>
              
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                      placeholder="email@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                      placeholder="+1 (234) 567-8910"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Project Duration
                    </label>
                    <select className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors">
                      <option value="">Select duration</option>
                      <option value="1-3 months">1-3 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6-12 months">6-12 months</option>
                      <option value="12+ months">12+ months</option>
                      <option value="ongoing">Ongoing</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Number of Positions
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                      placeholder="Number of contract positions needed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Required Skills & Experience *
                  </label>
                  <textarea
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                    placeholder="Describe the skills, experience, and qualifications required for the contract positions..."
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Project Details
                  </label>
                  <textarea
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                    placeholder="Provide additional details about the project, timeline, and any specific requirements..."
                  />
                </div>

                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-8 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors shadow-lg"
                  >
                    Request Contract Staffing Services
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default ContractStaffingPage; 